import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import ReactMapGL, { Marker, NavigationControl, LinearInterpolator } from "react-map-gl";
import Supercluster from "supercluster";
import WebMercatorViewport from "viewport-mercator-project";
import Tooltip from "@material-ui/core/Tooltip";
import { makeStyles } from "@material-ui/core/styles";
import mapboxgl from "mapbox-gl";
import { useMapLanguage } from "hooks/useMapLanguage";
import MapboxWorker from "mapbox-gl/dist/mapbox-gl-csp-worker?worker";
import { isMapPointHighlighted } from "util/zipcodeVisualization";
import ZipcodeMapMarker from "./ZipcodeMapMarker";

mapboxgl.workerClass = MapboxWorker;

const MAPBOX_API_ACCESS_TOKEN = import.meta.env.VITE_MAPBOX_API_ACCESS_TOKEN;
const MAPBOX_MAP_STYLE = import.meta.env.VITE_MAPBOX_WHITE_MAP_STYLE;

const DEFAULT_LATITUDE = 51.1657;
const DEFAULT_LONGITUDE = 10.4515;
const DEFAULT_ZOOM = 6;
const VIEWPORT_TRANSITION_MS = 300;

const usePinTooltipStyles = makeStyles({
  tooltip: {
    backgroundColor: "#000 !important",
    color: "#fff !important",
    fontSize: "15px !important",
    fontWeight: "700 !important",
    padding: "8px 12px !important",
    borderRadius: "4px !important",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.45) !important",
  },
  arrow: {
    color: "#000 !important",
  },
});

function withViewportTransition(viewport) {
  return {
    ...viewport,
    transitionDuration: VIEWPORT_TRANSITION_MS,
    transitionInterpolator: new LinearInterpolator(),
    transitionEasing: (t) => t,
  };
}

function viewportForProjection(viewport, width, height) {
  return new WebMercatorViewport({
    width,
    height,
    latitude: viewport.latitude,
    longitude: viewport.longitude,
    zoom: viewport.zoom,
    bearing: viewport.bearing || 0,
    pitch: viewport.pitch || 0,
  });
}

function bboxForPoints(points, padding = 0.25) {
  const lngs = points.map((p) => p.longitude);
  const lats = points.map((p) => p.latitude);
  return [
    Math.min(...lngs) - padding,
    Math.min(...lats) - padding,
    Math.max(...lngs) + padding,
    Math.max(...lats) + padding,
  ];
}

export default function ZipcodesVisualizationMap({
  points,
  hoveredId,
  selectedId,
  selectionKey = "",
  onHover,
  onSelect,
}) {
  const mapboxRef = useRef(null);
  const containerRef = useRef(null);
  const ignoreViewportSyncRef = useRef(false);
  const [mapReady, setMapReady] = useState(false);
  const [mapSize, setMapSize] = useState({ width: 800, height: 500 });
  const [viewport, setViewport] = useState({
    latitude: DEFAULT_LATITUDE,
    longitude: DEFAULT_LONGITUDE,
    zoom: DEFAULT_ZOOM,
  });

  useMapLanguage(mapboxRef);
  const pinTooltipClasses = usePinTooltipStyles();
  const tooltipClasses = {
    tooltip: pinTooltipClasses.tooltip,
    arrow: pinTooltipClasses.arrow,
  };

  const superclusterIndex = useMemo(() => {
    const index = new Supercluster({ radius: 56, maxZoom: 16 });
    const features = (points || []).map((point) => ({
      type: "Feature",
      properties: { ...point },
      geometry: {
        type: "Point",
        coordinates: [point.longitude, point.latitude],
      },
    }));
    index.load(features);
    return index;
  }, [points]);

  const clusters = useMemo(() => {
    if (!points?.length || mapSize.width <= 0) {
      return [];
    }
    const vp = viewportForProjection(viewport, mapSize.width, mapSize.height);
    const [vpWest, vpSouth, vpEast, vpNorth] = vp.getBounds();
    const [ptWest, ptSouth, ptEast, ptNorth] = bboxForPoints(points);
    const west = Math.min(vpWest, ptWest);
    const south = Math.min(vpSouth, ptSouth);
    const east = Math.max(vpEast, ptEast);
    const north = Math.max(vpNorth, ptNorth);
    const zoom = Math.max(0, Math.floor(viewport.zoom || DEFAULT_ZOOM));
    return superclusterIndex.getClusters([west, south, east, north], zoom);
  }, [points, superclusterIndex, viewport, mapSize.width, mapSize.height]);

  const fitKey = useMemo(
    () => `${selectionKey}:${(points || []).map((p) => p.id).join("|")}`,
    [selectionKey, points],
  );

  const applyFitToPoints = useCallback(
    (animate) => {
      if (!points?.length) {
        setViewport({
          latitude: DEFAULT_LATITUDE,
          longitude: DEFAULT_LONGITUDE,
          zoom: DEFAULT_ZOOM,
          bearing: 0,
          pitch: 0,
          transitionDuration: 0,
          transitionInterpolator: undefined,
        });
        return;
      }

      const countryCounts = {};
      points.forEach((p) => {
        const country = p.country || "unknown";
        countryCounts[country] = (countryCounts[country] || 0) + 1;
      });

      let dominantCountry = null;
      let maxCount = 0;
      Object.entries(countryCounts).forEach(([country, count]) => {
        if (count > maxCount) {
          maxCount = count;
          dominantCountry = country;
        }
      });

      const fitPoints = points.filter((p) => (p.country || "unknown") === dominantCountry);

      const width = mapSize.width || 800;
      const height = mapSize.height || 500;
      const bounds = new mapboxgl.LngLatBounds();
      fitPoints.forEach((p) => {
        bounds.extend([p.longitude, p.latitude]);
      });

      const mercator = new WebMercatorViewport({ width, height, bearing: 0, pitch: 0 });
      const [[west, south], [east, north]] = bounds.toArray();
      const fitted = mercator.fitBounds(
        [
          [west, south],
          [east, north],
        ],
        { padding: 64, maxZoom: 12 },
      );

      const map = mapboxRef.current;
      if (map) {
        ignoreViewportSyncRef.current = true;
        map.fitBounds(bounds, {
          padding: 64,
          duration: animate ? 500 : 0,
          maxZoom: 12,
        });
        window.setTimeout(() => {
          ignoreViewportSyncRef.current = false;
        }, animate ? 550 : 50);
      }

      const nextViewport = {
        latitude: fitted.latitude,
        longitude: fitted.longitude,
        zoom: fitted.zoom,
        bearing: 0,
        pitch: 0,
        transitionDuration: 0,
        transitionInterpolator: undefined,
      };
      if (animate) {
        setViewport((prev) => withViewportTransition({ ...prev, ...nextViewport }));
      } else {
        setViewport(nextViewport);
      }
    },
    [points, mapSize.width, mapSize.height],
  );

  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) {
      return undefined;
    }
    const updateSize = () => {
      setMapSize({
        width: element.offsetWidth || 800,
        height: element.offsetHeight || 500,
      });
    };
    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const prevFitKeyRef = useRef("");

  useEffect(() => {
    if (!mapReady || mapSize.width <= 0) {
      return;
    }
    const animate = prevFitKeyRef.current !== "" && prevFitKeyRef.current !== fitKey;
    prevFitKeyRef.current = fitKey;
    applyFitToPoints(animate);
  }, [fitKey, mapReady, mapSize.width, mapSize.height, applyFitToPoints]);

  const handleViewportChange = (nextViewport) => {
    if (ignoreViewportSyncRef.current) {
      return;
    }
    setViewport(nextViewport);
  };

  const handleTransitionEnd = () => {
    setViewport((prev) => ({
      ...prev,
      transitionDuration: 0,
      transitionInterpolator: undefined,
    }));
  };

  const useDirectMarkers = points.length > 0 && clusters.length === 0;

  const directMarkerLayers = useMemo(() => {
    if (!useDirectMarkers) {
      return { regular: [], highlighted: [] };
    }
    const regular = [];
    const highlighted = [];
    for (const point of points || []) {
      if (isMapPointHighlighted(point, hoveredId, selectedId)) {
        highlighted.push(point);
      } else {
        regular.push(point);
      }
    }
    return { regular, highlighted };
  }, [points, hoveredId, selectedId, useDirectMarkers]);

  const clusterMarkerLayers = useMemo(() => {
    if (useDirectMarkers) {
      return { clusters: [], regular: [], highlighted: [] };
    }
    const clusterItems = [];
    const regular = [];
    const highlighted = [];
    for (const cluster of clusters) {
      if (cluster.properties?.cluster === true) {
        clusterItems.push(cluster);
        continue;
      }
      const point = cluster.properties;
      if (!point?.zipcode) {
        continue;
      }
      if (isMapPointHighlighted(point, hoveredId, selectedId)) {
        highlighted.push(cluster);
      } else {
        regular.push(cluster);
      }
    }
    return { clusters: clusterItems, regular, highlighted };
  }, [clusters, hoveredId, selectedId, useDirectMarkers]);

  const renderMapPointMarker = (point, longitude, latitude) => (
    <Marker
      key={`${point.id}-${longitude}-${latitude}`}
      latitude={latitude}
      longitude={longitude}
      offsetTop={-20}
      offsetLeft={-10}
    >
      <ZipcodeMapMarker
        point={point}
        highlighted={isMapPointHighlighted(point, hoveredId, selectedId)}
        tooltipClasses={tooltipClasses}
        onHover={onHover}
        onSelect={onSelect}
      />
    </Marker>
  );

  const handleClusterClick = (cluster) => {
    const [longitude, latitude] = cluster.geometry.coordinates;
    const expansionZoom = Math.min(
      superclusterIndex.getClusterExpansionZoom(cluster.id),
      16,
    );
    setViewport((prev) =>
      withViewportTransition({
        ...prev,
        latitude,
        longitude,
        zoom: expansionZoom,
      }),
    );
  };

  return (
    <div ref={containerRef} style={{ width: "100%", height: "100%" }}>
      <ReactMapGL
        width="100%"
        height="100%"
        {...viewport}
        onViewportChange={handleViewportChange}
        onTransitionEnd={handleTransitionEnd}
        mapStyle={MAPBOX_MAP_STYLE}
        mapboxApiAccessToken={MAPBOX_API_ACCESS_TOKEN}
        onLoad={(event) => {
          mapboxRef.current = event.target;
          setMapReady(true);
          if (containerRef.current) {
            setMapSize({
              width: containerRef.current.offsetWidth,
              height: containerRef.current.offsetHeight,
            });
          }
          if (points?.length) {
            requestAnimationFrame(() => applyFitToPoints(false));
          }
        }}
      >
        <div style={{ position: "absolute", bottom: 70, right: 40, zIndex: 2 }}>
          <NavigationControl showCompass={false} />
        </div>
        {useDirectMarkers &&
          directMarkerLayers.regular.map((point) =>
            renderMapPointMarker(point, point.longitude, point.latitude),
          )}
        {useDirectMarkers &&
          directMarkerLayers.highlighted.map((point) =>
            renderMapPointMarker(point, point.longitude, point.latitude),
          )}
        {!useDirectMarkers &&
          clusterMarkerLayers.clusters.map((cluster) => {
            const [longitude, latitude] = cluster.geometry.coordinates;
            const pointCount = cluster.properties?.point_count;

            return (
              <Marker
                key={`cluster-${cluster.id}`}
                latitude={latitude}
                longitude={longitude}
                offsetLeft={-16}
                offsetTop={-16}
              >
                <Tooltip
                  title={`${pointCount} zipcodes`}
                  placement="top"
                  arrow
                  classes={tooltipClasses}
                  PopperProps={{ style: { zIndex: 20 } }}
                >
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => handleClusterClick(cluster)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleClusterClick(cluster);
                      }
                    }}
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      background: "#3d405b",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 12,
                      fontWeight: 700,
                      border: "2px solid #fff",
                      cursor: "pointer",
                    }}
                  >
                    {pointCount}
                  </div>
                </Tooltip>
              </Marker>
            );
          })}
        {!useDirectMarkers &&
          clusterMarkerLayers.regular.map((cluster) => {
            const [longitude, latitude] = cluster.geometry.coordinates;
            const point = cluster.properties;
            return renderMapPointMarker(point, longitude, latitude);
          })}
        {!useDirectMarkers &&
          clusterMarkerLayers.highlighted.map((cluster) => {
            const [longitude, latitude] = cluster.geometry.coordinates;
            const point = cluster.properties;
            return renderMapPointMarker(point, longitude, latitude);
          })}
      </ReactMapGL>
    </div>
  );
}
